import { useCallback, useEffect, useState } from 'react';
import OlVectorSource from 'ol/source/Vector';
import Style from 'ol/style/Style';
import { Fill, Stroke, Text } from 'ol/style';
import { Cluster } from 'ol/source';
import CircleStyle from 'ol/style/Circle';
import { useOpenLayers } from '../openlayers-context';
import DoubleClickZoom from 'ol/interaction/DoubleClickZoom';
import useGeometrySelect from '../../hooks/useGeometrySelect';
import useResetSelection from '../../hooks/useResetSelection';

/**
 * Cluster controls logic, styles reside in this hook
 */
var useClusterSource = _ref => {
  var _styles$image4, _styles$image5, _styles$image6, _styles$image6$stroke, _styles$text3, _styles$text4, _selectedStyles$image6, _selectedStyles$image7, _selectedStyles$image8, _selectedStyles$image9, _selectedStyles$text3, _selectedStyles$text4;

  var {
    vectorLayer,
    features,
    clusterDistance,
    onSelect,
    minDistance = 0,
    multiSelect,
    styles,
    selectedStyles,
    displaySize,
    canSelect
  } = _ref;
  var [vectorSource, setVectorSource] = useState();
  var [clusterSource, setClusterSource] = useState();
  var {
    map
  } = useOpenLayers();
  var resetSelection = useResetSelection('cluster-select');

  var mapFeatures = clusterFeatures => {
    var _clusterFeatures$map$, _clusterFeatures$map;

    return (_clusterFeatures$map$ = (_clusterFeatures$map = clusterFeatures.map(cluster => cluster === null || cluster === void 0 ? void 0 : cluster.get('features'))) === null || _clusterFeatures$map === void 0 ? void 0 : _clusterFeatures$map.flat(1)) !== null && _clusterFeatures$map$ !== void 0 ? _clusterFeatures$map$ : [];
  };

  var setStyles = useCallback(feat => {
    var _styles$image$radius, _styles$image, _styles$image2, _styles$image2$stroke, _styles$image3, _styles$text, _styles$text2;

    var size = feat.get('features').length;
    return new Style({
      image: new CircleStyle({
        radius: (_styles$image$radius = (_styles$image = styles.image) === null || _styles$image === void 0 ? void 0 : _styles$image.radius) !== null && _styles$image$radius !== void 0 ? _styles$image$radius : 0,
        stroke: new Stroke({
          color: (_styles$image2 = styles.image) === null || _styles$image2 === void 0 ? void 0 : (_styles$image2$stroke = _styles$image2.stroke) === null || _styles$image2$stroke === void 0 ? void 0 : _styles$image2$stroke.color
        }),
        fill: new Fill({
          color: (_styles$image3 = styles.image) === null || _styles$image3 === void 0 ? void 0 : _styles$image3.fill.color
        })
      }),
      text: new Text({
        text: displaySize ? size.toString() : (_styles$text = styles.text) === null || _styles$text === void 0 ? void 0 : _styles$text.text,
        fill: new Fill({
          color: (_styles$text2 = styles.text) === null || _styles$text2 === void 0 ? void 0 : _styles$text2.fill.color
        })
      })
    });
  }, [displaySize, (_styles$image4 = styles.image) === null || _styles$image4 === void 0 ? void 0 : _styles$image4.fill.color, (_styles$image5 = styles.image) === null || _styles$image5 === void 0 ? void 0 : _styles$image5.radius, (_styles$image6 = styles.image) === null || _styles$image6 === void 0 ? void 0 : (_styles$image6$stroke = _styles$image6.stroke) === null || _styles$image6$stroke === void 0 ? void 0 : _styles$image6$stroke.color, (_styles$text3 = styles.text) === null || _styles$text3 === void 0 ? void 0 : _styles$text3.fill.color, (_styles$text4 = styles.text) === null || _styles$text4 === void 0 ? void 0 : _styles$text4.text]);
  var setSelectedStyles = useCallback(feat => {
    var _selectedStyles$image, _selectedStyles$image2, _selectedStyles$image3, _selectedStyles$image4, _selectedStyles$image5, _selectedStyles$text, _selectedStyles$text2;

    var size = feat.get('features').length;
    return new Style({
      image: new CircleStyle({
        radius: (_selectedStyles$image = (_selectedStyles$image2 = selectedStyles.image) === null || _selectedStyles$image2 === void 0 ? void 0 : _selectedStyles$image2.radius) !== null && _selectedStyles$image !== void 0 ? _selectedStyles$image : 0,
        stroke: new Stroke({
          color: (_selectedStyles$image3 = selectedStyles.image) === null || _selectedStyles$image3 === void 0 ? void 0 : (_selectedStyles$image4 = _selectedStyles$image3.stroke) === null || _selectedStyles$image4 === void 0 ? void 0 : _selectedStyles$image4.color
        }),
        fill: new Fill({
          color: (_selectedStyles$image5 = selectedStyles.image) === null || _selectedStyles$image5 === void 0 ? void 0 : _selectedStyles$image5.fill.color
        })
      }),
      text: new Text({
        text: displaySize ? size === null || size === void 0 ? void 0 : size.toString() : (_selectedStyles$text = selectedStyles.text) === null || _selectedStyles$text === void 0 ? void 0 : _selectedStyles$text.text,
        fill: new Fill({
          color: (_selectedStyles$text2 = selectedStyles.text) === null || _selectedStyles$text2 === void 0 ? void 0 : _selectedStyles$text2.fill.color
        })
      })
    });
  }, [displaySize, (_selectedStyles$image6 = selectedStyles.image) === null || _selectedStyles$image6 === void 0 ? void 0 : _selectedStyles$image6.fill.color, (_selectedStyles$image7 = selectedStyles.image) === null || _selectedStyles$image7 === void 0 ? void 0 : _selectedStyles$image7.radius, (_selectedStyles$image8 = selectedStyles.image) === null || _selectedStyles$image8 === void 0 ? void 0 : (_selectedStyles$image9 = _selectedStyles$image8.stroke) === null || _selectedStyles$image9 === void 0 ? void 0 : _selectedStyles$image9.color, (_selectedStyles$text3 = selectedStyles.text) === null || _selectedStyles$text3 === void 0 ? void 0 : _selectedStyles$text3.fill.color, (_selectedStyles$text4 = selectedStyles.text) === null || _selectedStyles$text4 === void 0 ? void 0 : _selectedStyles$text4.text]);
  useEffect(() => {
    var newVectorSource = new OlVectorSource();
    setVectorSource(newVectorSource);
    return () => {
      newVectorSource.dispose();
    };
  }, []); // Assign cluster to vector layer

  useEffect(() => {
    if (vectorSource && clusterSource && vectorLayer) {
      clusterSource.setSource(vectorSource);
      vectorLayer.setSource(clusterSource);
    }
  }, [vectorSource, clusterSource, vectorLayer]); // Set initial cluster styles

  useEffect(() => {
    vectorLayer === null || vectorLayer === void 0 ? void 0 : vectorLayer.setStyle(setStyles);
  }, [setStyles, styles, vectorLayer]); // Add features to vector source

  useEffect(() => {
    if (features && vectorSource) {
      vectorSource.clear();
      vectorSource.addFeatures(features);
    }
  }, [features, vectorSource]);
  /**
   * Initiate Cluster and set cluster configs
   * On component unmount remove the clusterSource from the vector layer
   */

  useEffect(() => {
    var clusterConfig = {
      minDistance,
      distance: clusterDistance
    };
    var source = new Cluster(clusterConfig);
    setClusterSource(source);
    return () => {
      source.dispose();
    };
  }, [vectorLayer, setStyles, minDistance, clusterDistance]);
  /**
   * Remove doubleclick zoom interaction
   */

  useEffect(() => {
    if (!map) return;
    var interactions = map.getInteractions();
    interactions.forEach(interaction => {
      if (interaction instanceof DoubleClickZoom) {
        map.removeInteraction(interaction);
      }
    });
  }, [map]);
  useEffect(() => {
    if (!map) return;
    var currentZoom = map.getView().getZoom();
    /**
     * On zoom value change reset selected cluster features
     * As zoom value changes clusters rendered on map are different
     * At this point selectedClustersList contains features which are not on the map anymore
     * Because of this we reset the selected cluster features
     */

    var onMoveEnd = () => {
      var newZoom = map.getView().getZoom();

      if (currentZoom !== newZoom) {
        // when user updates zoom, assign newZoom value to currentZoom to keep it upto date
        currentZoom = newZoom;
        resetSelection();
      }
    };

    map.on('moveend', onMoveEnd);
    return () => {
      map.un('moveend', onMoveEnd);
    };
  }, [map, resetSelection]);
  useGeometrySelect({
    layerId: vectorLayer === null || vectorLayer === void 0 ? void 0 : vectorLayer.get('id'),
    onSelect,
    selectStyles: setSelectedStyles,
    canSelect,
    multiSelect,
    interactionId: 'cluster-select',
    mapFeaturesFunc: mapFeatures
  });
  return null;
};

export default useClusterSource;